/**
 * "Try an answer" — the manual probe, PRD §3.18.
 *
 * Visual Reference: `design/screens/editor-qualify.html`, the **Try an answer**
 * block and its result.
 *
 * IT IS NOT A SIMULATION. `POST …/probe` runs `packages/schema`'s `probe()` —
 * `score()` then `verdict()`, in the order the submission path calls them — so
 * an operator calibrating here is calibrating against the scorer that will
 * actually run. A probe with a code path of its own is worse than no probe.
 *
 * THE GLYPHS ARE NOT DECIDED HERE. DECISION-026 fixes them as a pure function
 * of `points_awarded ÷ points_max`, and `evidenceStrength` in
 * `packages/schema/src/verdict.ts` is that function — the same one the lead
 * drawer will call in Story 8.4, so a row reading "partly met" and a drawer
 * glyph reading "met" about the same criterion is not a thing that can happen.
 *
 * IT SAVES NOTHING, and says so where the export says so.
 *
 * Story 7.3.
 */
import { useState } from 'react'
import {
  evidenceStrength,
  type Block,
  type Contribution,
  type EvidenceStrength,
  type Funnel,
} from '@leadgible/schema'
import { ApiFailure, apiPost } from '../lib/api'
import { spell } from '../lib/words'
import { useShell } from '../shell/workspace'
import { Callout } from '../ui/Callout'
import { Pill } from '../ui/Pill'
import { Select } from '../ui/Select'
import { useEditor } from './store'

const NOT_SAVED = 'Nothing is saved to your leads'

type DisplayAnswer = { blockId: string; question: string; display: string }

type ProbeResult = {
  score: number
  band: { id: string; label: string; tone: 'nurture' | 'review' | 'hot'; min: number; max: number }
  is_qualified: boolean
  contributions: Contribution[]
  disqualified_by: string | null
  disqualified_reason: string | null
  verdict: { sentence: string; reason: string }
  answers: DisplayAnswer[]
}

/**
 * DECISION-026's four strengths, as the export draws them: a 26px square in the
 * band colour the strength corresponds to. The mark is the glyph, not a word,
 * because the row already says the words.
 */
const GLYPH: Record<EvidenceStrength, { mark: string; tone: string }> = {
  met: { mark: '✓', tone: 'bg-mdx-hot text-mdx-white' },
  partial: { mark: '◐', tone: 'bg-mdx-review text-mdx-white' },
  weak: { mark: '−', tone: 'bg-mdx-nurture text-mdx-white' },
  absent: { mark: '?', tone: 'bg-mdx-line text-mdx-slate' },
}

/** The band tone, as a `Pill`. RULE-002: no red anywhere in the ladder. */
const BAND_PILL: Record<'nurture' | 'review' | 'hot', 'neutral' | 'review' | 'hot'> = {
  nurture: 'neutral',
  review: 'review',
  hot: 'hot',
}

export function Probe({ funnelId }: { funnelId: string }) {
  const { workspace } = useShell()
  const workspaceId = workspace?.id ?? null
  const funnel = useEditor((s) => s.funnel)

  const [picked, setPicked] = useState<Record<string, string>>({})
  const [result, setResult] = useState<ProbeResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [running, setRunning] = useState(false)

  if (!funnel) return null

  const asked = funnel.blocks.filter((block) => 'question' in block)

  const run = async () => {
    if (!workspaceId) return
    setRunning(true)
    setError(null)
    try {
      setResult(
        await apiPost<ProbeResult>(`/v1/w/${workspaceId}/funnels/${funnelId}/probe`, {
          answers: answersFrom(asked, picked),
        }),
      )
    } catch (e) {
      setError(
        e instanceof ApiFailure
          ? e.message
          : "We couldn't score that. Check your connection and try again.",
      )
    } finally {
      setRunning(false)
    }
  }

  return (
    <section className="flex flex-col gap-mdx-20 rounded-mdx-card border border-mdx-line bg-mdx-white p-mdx-24 shadow-mdx-card">
      <div className="flex flex-wrap items-baseline justify-between gap-x-mdx-16 gap-y-mdx-8">
        <span className="text-mdx-17 font-mdx-semibold tracking-mdx-h3">Try an answer</span>
        {/* Where the export says it, and the reason the panel is safe to play in. */}
        <span className="text-mdx-13 text-mdx-slate">{NOT_SAVED}</span>
      </div>

      {asked.length === 0 ? (
        <span className="text-mdx-13 text-mdx-slate">
          Add a question on the Build tab and you can try an answer here.
        </span>
      ) : (
        <div className="flex flex-col gap-mdx-12">
          {asked.map((block) => (
            <Ask
              key={block.id}
              block={block}
              value={picked[block.id] ?? ''}
              onChange={(next) => setPicked({ ...picked, [block.id]: next })}
              funnel={funnel}
            />
          ))}
        </div>
      )}

      {error ? <Callout>{error}</Callout> : null}

      <button
        type="button"
        onClick={() => void run()}
        disabled={running || asked.length === 0}
        className="cursor-pointer self-start rounded-mdx-control border-0 bg-mdx-brand px-mdx-24 py-mdx-12 text-mdx-15 font-mdx-semibold text-mdx-white disabled:cursor-not-allowed disabled:opacity-mdx-recessed"
      >
        {running ? 'Scoring…' : 'See the score'}
      </button>

      {result ? <Result result={result} funnel={funnel} /> : null}
    </section>
  )
}

/* ── the form ───────────────────────────────────────────────────────────── */

function Ask({
  block,
  value,
  onChange,
  funnel,
}: {
  block: Block
  value: string
  onChange: (next: string) => void
  funnel: Funnel
}) {
  const question = 'question' in block ? block.question : block.id
  const options = 'options' in block ? block.options : undefined

  if (options && options.length > 0) {
    return (
      <Select label={question} showLabel value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">No answer</option>
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.label}
          </option>
        ))}
      </Select>
    )
  }

  /**
   * A free-text answer is where extraction reads from, and the export says so
   * under the field: "Experience is read from this answer." It is rendered
   * because the operator needs to know why the box is there — the probe scores
   * no extraction (the AI does not run here), and the bench's stated range is
   * what covers that.
   */
  const reads = funnel.rubric.extraction.filter((field) => field.block_id === block.id)

  return (
    <label className="flex flex-col gap-mdx-6">
      <span className="text-mdx-14 font-mdx-semibold">{question}</span>
      <textarea
        value={value}
        onChange={(event) => onChange(event.target.value)}
        rows={2}
        className="min-h-mdx-textarea-min rounded-mdx-control border border-mdx-line bg-mdx-white px-mdx-14 py-mdx-10 text-mdx-15 text-mdx-ink outline-none focus:border-mdx-brand"
      />
      {reads.map((field) => (
        <span key={field.field_key} className="text-mdx-13 text-mdx-slate">
          {field.label} is read from this answer.
        </span>
      ))}
    </label>
  )
}

/** The answers as the submission path shapes them — `answerSchema`, not a form. */
function answersFrom(blocks: Block[], picked: Record<string, string>) {
  const at = Date.now()
  return blocks
    .filter((block) => (picked[block.id] ?? '') !== '')
    .map((block) => ({
      block_id: block.id,
      type: block.type,
      value: picked[block.id]!,
      label: 'question' in block ? block.question : block.id,
      answered_at: at,
    }))
}

/* ── the result ─────────────────────────────────────────────────────────── */

function Result({ result, funnel }: { result: ProbeResult; funnel: Funnel }) {
  const answerFor = new Map(result.answers.map((answer) => [answer.blockId, answer.display]))
  // criterion → block, from the rubric. `Contribution` carries no block id and
  // guessing one from the criterion id would break the moment an operator
  // renamed anything.
  const blockOf = new Map(
    funnel.rubric.criteria.flatMap((c) => (c.source === 'answer_ref' ? [[c.id, c.block_id]] : [])),
  )

  return (
    <div className="flex flex-col gap-mdx-16 border-t border-mdx-line-soft pt-mdx-20">
      {/* PRD §1.9 again: the sentence leads, the pill labels it, the number is
          the last thing on the panel rather than the first. */}
      <div className="flex flex-wrap items-center gap-mdx-12">
        <span className="text-mdx-17 font-mdx-semibold tracking-mdx-h3">
          {result.verdict.sentence}
        </span>
        <Pill tone={BAND_PILL[result.band.tone]} dot>
          {result.band.label}
        </Pill>
      </div>

      <p className="m-0 max-w-mdx-measure-56 text-mdx-15 text-mdx-slate">{result.verdict.reason}</p>

      {result.contributions.length > 0 ? (
        <div className="flex flex-col gap-mdx-8">
          {result.contributions.map((contribution) => (
            <Evidence
              key={contribution.criterion_id}
              contribution={contribution}
              answer={
                contribution.source === 'answer_ref'
                  ? (answerFor.get(blockOf.get(contribution.criterion_id) ?? '') ?? 'No answer')
                  : sourceNote(contribution.source)
              }
            />
          ))}
        </div>
      ) : (
        /*
          A disqualifier fired, and `contributions` is empty by design — the
          export calls disqualifiers "Hard gates — no score is calculated", and
          zeroed rows would claim every criterion was evaluated and found
          wanting, which is a different and false statement.
        */
        <span className="text-mdx-15 text-mdx-slate">
          A hard gate stopped this one, so no criterion was scored.
        </span>
      )}

      <p className="m-0 text-mdx-15">
        <strong className="font-mdx-semibold tabular-nums">{result.score}</strong>
        {distanceLine(result, funnel)}
      </p>
    </div>
  )
}

const sourceNote = (source: Contribution['source']): string =>
  source === 'extracted'
    ? 'read by the AI — not run here'
    : source === 'transcript'
      ? 'from a call — not run here'
      : 'from the visit'

function Evidence({ contribution, answer }: { contribution: Contribution; answer: string }) {
  const strength = evidenceStrength(contribution.points_awarded, contribution.points_max)
  const { mark, tone } = GLYPH[strength]

  return (
    <div className="flex items-center gap-mdx-16 rounded-mdx-control bg-mdx-ground px-mdx-16 py-mdx-12">
      <span
        aria-hidden="true"
        className={`flex size-mdx-evidence-icon flex-none items-center justify-center rounded-mdx-control-sm text-mdx-14 font-mdx-bold ${tone}`}
      >
        {mark}
      </span>
      <span className="flex-none text-mdx-15 font-mdx-semibold">{contribution.criterion_label}</span>
      <span className="min-w-0 flex-1 truncate text-mdx-15 text-mdx-slate">{answer}</span>
      <span className="flex-none whitespace-nowrap text-mdx-14 font-mdx-semibold text-mdx-slate tabular-nums">
        {contribution.points_awarded} / {contribution.points_max}
      </span>
      {/* The strength in words, for a reader who cannot see the glyph. */}
      <span className="sr-only">{strength}</span>
    </div>
  )
}

/**
 * "— five points short of your Hot threshold of 70."
 *
 * The distance to the NEXT band up, NAMED, because that is the question an
 * operator is asking of a result they are not happy with — and "the next band"
 * is a worse answer than "Hot" to somebody who wrote the ladder themselves.
 * At the top band there is no next one and the sentence stops after the score.
 */
function distanceLine(result: ProbeResult, funnel: Funnel): string {
  if (result.disqualified_by) return ' — stopped by a hard gate before scoring.'

  const next = [...funnel.rubric.bands]
    .sort((a, b) => a.min - b.min)
    .find((band) => band.min > result.score)
  if (!next) return '.'

  const gap = next.min - result.score
  return ` — ${spell(gap)} ${gap === 1 ? 'point' : 'points'} short of your ${next.label} threshold of ${next.min}.`
}
